import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Row,
  Col,
  InputGroup,
  Form,
  Pagination,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  listarTiposContaInterna,
  excluirTipoContaInterna,
} from '~/Services/Service';
import ModalTipoContaInterna from './ModalTipoContaInterna';

const ITENS_POR_PAGINA = 20;

const formatarFlag = (valor) => {
  if (valor === 'S') return 'Sim';
  if (valor === 'N') return 'Não';
  return '—';
};

const TipoContaInterna = () => {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [confirmacao, setConfirmacao] = useState({ show: false, item: null });
  const [filtro, setFiltro] = useState('');
  const [ordenacao, setOrdenacao] = useState({
    campo: 'codigo',
    direcao: 'asc',
  });
  const [pagina, setPagina] = useState(1);

  const carregarTipos = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await listarTiposContaInterna();
      setTipos(resp || []);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarTipos();
  }, [carregarTipos]);

  const handleOrdenacao = (campo) => {
    setOrdenacao((prev) => {
      if (prev.campo === campo) {
        return {
          campo,
          direcao: prev.direcao === 'asc' ? 'desc' : 'asc',
        };
      }
      return { campo, direcao: 'asc' };
    });
    setPagina(1);
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) {
      return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    }
    return ordenacao.direcao === 'asc' ? (
      <FaSortUp style={{ marginLeft: 4 }} />
    ) : (
      <FaSortDown style={{ marginLeft: 4 }} />
    );
  };

  const thSortStyle = {
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };

  const handleNovo = () => {
    setItemSelecionado(null);
    setShowModal(true);
  };

  const handleEditar = (item) => {
    setItemSelecionado(item);
    setShowModal(true);
  };

  const handleExcluir = (item) => {
    setConfirmacao({ show: true, item });
  };

  const handleConfirmarExclusao = async (confirmado) => {
    if (!confirmado) {
      setConfirmacao({ show: false, item: null });
      return;
    }
    const item = confirmacao.item;
    setConfirmacao({ show: false, item: null });
    try {
      await excluirTipoContaInterna({ codigo: item.codigo });
      toast.success('Tipo excluído com sucesso.');
      const proximoTotal = Math.max(0, totalRegistros - 1);
      const proximaUltimaPagina = Math.max(
        1,
        Math.ceil(proximoTotal / ITENS_POR_PAGINA)
      );
      setPagina((atual) => Math.min(atual, proximaUltimaPagina));
      await carregarTipos();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  const handleSalvar = () => {
    setShowModal(false);
    carregarTipos();
  };

  const handleFecharModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleFiltro = (e) => {
    setFiltro(e.target.value);
    setPagina(1);
  };

  const tiposFiltrados = useMemo(() => {
    return tipos.filter((item) => {
      if (!filtro) return true;
      const termo = filtro.toLowerCase();
      return (
        (item.descricao || '').toLowerCase().indexOf(termo) >= 0 ||
        String(item.codigo || '').indexOf(termo) >= 0
      );
    });
  }, [tipos, filtro]);

  const tiposOrdenados = useMemo(() => {
    return tiposFiltrados.slice().sort((a, b) => {
      const campo = ordenacao.campo;
      const dir = ordenacao.direcao === 'asc' ? 1 : -1;
      let valA = a[campo];
      let valB = b[campo];
      if (valA == null) valA = '';
      if (valB == null) valB = '';
      if (typeof valA === 'string') {
        return dir * valA.toLowerCase().localeCompare(String(valB).toLowerCase());
      }
      return dir * (valA - valB);
    });
  }, [tiposFiltrados, ordenacao]);

  const totalRegistros = tiposOrdenados.length;
  const totalPaginas = Math.max(
    1,
    Math.ceil(totalRegistros / ITENS_POR_PAGINA)
  );
  const paginaSegura = Math.min(pagina, totalPaginas);
  const tiposPaginados = tiposOrdenados.slice(
    (paginaSegura - 1) * ITENS_POR_PAGINA,
    paginaSegura * ITENS_POR_PAGINA
  );

  const mudarPagina = (novaPagina) => {
    if (novaPagina < 1 || novaPagina > totalPaginas) return;
    setPagina(novaPagina);
  };

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;

    const maxBotoes = 5;
    const itens = [];

    let inicioPaginas = Math.max(
      1,
      paginaSegura - Math.floor(maxBotoes / 2)
    );
    let fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);
    if (fimPaginas - inicioPaginas < maxBotoes - 1) {
      inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
    }

    itens.push(
      <Pagination.First
        key="first"
        onClick={() => {
          mudarPagina(1);
        }}
        disabled={paginaSegura === 1}
      />
    );
    itens.push(
      <Pagination.Prev
        key="prev"
        onClick={() => {
          mudarPagina(paginaSegura - 1);
        }}
        disabled={paginaSegura === 1}
      />
    );

    if (inicioPaginas > 1) {
      itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let n = inicioPaginas; n <= fimPaginas; n++) {
      const num = n;
      itens.push(
        <Pagination.Item
          key={num}
          active={num === paginaSegura}
          onClick={() => {
            mudarPagina(num);
          }}
        >
          {num}
        </Pagination.Item>
      );
    }

    if (fimPaginas < totalPaginas) {
      itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    itens.push(
      <Pagination.Next
        key="next"
        onClick={() => {
          mudarPagina(paginaSegura + 1);
        }}
        disabled={paginaSegura === totalPaginas}
      />
    );
    itens.push(
      <Pagination.Last
        key="last"
        onClick={() => {
          mudarPagina(totalPaginas);
        }}
        disabled={paginaSegura === totalPaginas}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2">
        <small className="text-muted">
          Mostrando{' '}
          {tiposPaginados.length > 0
            ? (paginaSegura - 1) * ITENS_POR_PAGINA + 1
            : 0}{' '}
          até{' '}
          {Math.min(paginaSegura * ITENS_POR_PAGINA, totalRegistros)} de{' '}
          {totalRegistros} registros
        </small>
        <Pagination size="sm" className="mb-0">
          {itens}
        </Pagination>
      </div>
    );
  };

  const itemExclusao = confirmacao.item;

  if (loading) return <Loading />;

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Tipos de Conta Interna" />

      <Card
        className="shadow-sm mb-3"
        style={{ maxWidth: '900px', margin: '0 auto' }}
      >
        <Card.Body className="py-2">
          <Row className="align-items-center">
            <Col md={4}>
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  placeholder="Filtrar por código ou descrição..."
                  value={filtro}
                  onChange={handleFiltro}
                />
              </InputGroup>
            </Col>
            <Col className="text-right">
              <Button size="sm" variant="primary" onClick={handleNovo}>
                <FaPlus className="mr-1" /> Novo Tipo
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card
        className="shadow-sm"
        style={{ maxWidth: '900px', margin: '0 auto' }}
      >
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Tipos de Conta Interna
            <span
              className="ml-2"
              style={{ fontSize: '0.85rem', opacity: 0.8 }}
            >
              ({totalRegistros}{' '}
              {totalRegistros === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
        </Card.Header>
        <Card.Body className="p-0">
          {totalRegistros === 0 ? (
            <p className="text-muted text-center my-4">
              Nenhum tipo cadastrado.
            </p>
          ) : (
            <>
              <div style={{ maxHeight: '25rem', overflowY: 'auto' }}>
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th
                        style={Object.assign(
                          {
                            width: '100px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: '#fff',
                          },
                          thSortStyle
                        )}
                        onClick={() => {
                          handleOrdenacao('codigo');
                        }}
                      >
                        Código{iconeOrdenacao('codigo')}
                      </th>
                      <th
                        style={Object.assign(
                          {
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: '#fff',
                          },
                          thSortStyle
                        )}
                        onClick={() => {
                          handleOrdenacao('descricao');
                        }}
                      >
                        Descrição{iconeOrdenacao('descricao')}
                      </th>
                      <th
                        style={Object.assign(
                          {
                            width: '130px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: '#fff',
                          },
                          thSortStyle
                        )}
                        onClick={() => {
                          handleOrdenacao('centro');
                        }}
                      >
                        Lança Centro{iconeOrdenacao('centro')}
                      </th>
                      <th
                        style={Object.assign(
                          {
                            width: '180px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: '#fff',
                          },
                          thSortStyle
                        )}
                        onClick={() => {
                          handleOrdenacao('bx_valida');
                        }}
                      >
                        Considera Baixa Válida
                        {iconeOrdenacao('bx_valida')}
                      </th>
                      <th
                        style={{
                          width: '90px',
                          textAlign: 'center',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                          backgroundColor: '#fff',
                        }}
                      >
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tiposPaginados.map((t) => (
                      <tr key={t.codigo}>
                        <td>{t.codigo}</td>
                        <td>{t.descricao}</td>
                        <td>{formatarFlag(t.centro)}</td>
                        <td>{formatarFlag(t.bx_valida)}</td>
                        <td
                          className="text-center"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <Button
                            size="sm"
                            variant="outline-warning"
                            title="Editar"
                            className="mr-1"
                            onClick={() => {
                              handleEditar(t);
                            }}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            title="Excluir"
                            onClick={() => {
                              handleExcluir(t);
                            }}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {renderPaginacao()}
            </>
          )}
        </Card.Body>
      </Card>

      <ModalConfirmacao
        show={confirmacao.show}
        title="Confirmar Exclusão"
        message={
          itemExclusao
            ? 'Deseja realmente excluir o tipo "' +
              (itemExclusao.descricao || '') +
              '"?'
            : ''
        }
        onClose={handleConfirmarExclusao}
      />

      <ModalTipoContaInterna
        show={showModal}
        onHide={handleFecharModal}
        tipo={itemSelecionado}
        onSalvar={handleSalvar}
      />
    </Container>
  );
};

export default TipoContaInterna;
